import type { ActionSubjectType } from '@trello/analytics-types';
import { Analytics, Apdex } from '@trello/atlassian-analytics';
import { Cookies } from '@trello/cookies';
// eslint-disable-next-line no-restricted-imports
import $ from '@trello/jquery';
import { getScreenFromUrl } from '@trello/marketing-screens';
import type { PIIString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import { TrelloStorage } from '@trello/storage';
import { importWithRetry } from '@trello/use-lazy-component';

import { errorPage } from 'app/scripts/controller/errorPage';
import { getHomeUrl } from 'app/scripts/controller/urls';
import { Auth } from 'app/scripts/db/Auth';
import { ModelCache } from 'app/scripts/db/ModelCache';
import { l } from 'app/scripts/lib/localize';
import type { Organization } from 'app/scripts/models/Organization';
import { setDocumentTitle } from 'app/src/components/DocumentTitle';
import {
  getHomeLastTab,
  getHomeLastTabStorageKey,
  isMemberOfOrg,
  redirectHomeToDefaultTab,
} from './memberPageHelpers';

// keeps track of whether we redirected on initial page load
let isInitial = false;

// Variables used to determine if we should pretend the user is new, for testing
// purposes
let simulateNewUser: string | null = null;

// keeps track if the browser forward or back buttons are used
let browserNav = false;
// eslint-disable-next-line @trello/no-module-logic
$(window).on('popstate', () => (browserNav = true));

const trackMemberHomeView = function (
  orgname: string | null | undefined,
  showHomeBoardsTab: boolean | undefined,
  templatesData: TemplatesData | undefined,
  showGettingStarted: boolean | undefined,
) {
  let actionSubject: ActionSubjectType | undefined;
  let context: { teamId?: string } = {};

  if (orgname && showHomeBoardsTab && !templatesData) {
    actionSubject = 'workspaceBoardsHomeScreen';
    // Being explicit about the type here because ModelCache is typed as `any`
    // since it's imported via `require`
    const org: Organization | undefined = ModelCache.findOne(
      'Organization',
      'name',
      orgname,
    );
    context = { teamId: org?.id };
  } else if (
    !orgname &&
    showHomeBoardsTab &&
    !templatesData &&
    !showGettingStarted
  ) {
    actionSubject = 'memberBoardsHomeScreen';
  } else if (
    !orgname &&
    !showHomeBoardsTab &&
    templatesData &&
    !showGettingStarted
  ) {
    actionSubject = 'templateGalleryScreen';
  } else if (
    !orgname &&
    !showHomeBoardsTab &&
    !templatesData &&
    !showGettingStarted
  ) {
    actionSubject = 'memberHomeScreen';
  } else if (
    showGettingStarted &&
    !orgname &&
    !showHomeBoardsTab &&
    !templatesData
  ) {
    actionSubject = 'workspaceGettingStartedScreen';
  } else {
    return;
  }

  return Analytics.sendOperationalEvent({
    action: 'rendered',
    actionSubject,
    source: getScreenFromUrl(),
    containers: {
      organization: {
        id: context?.teamId,
      },
    },
  });
};

interface TemplatesData {
  category: string;
  isInAppGallery: boolean;
  referrerUsername: string;
  shortLink?: string;
  templateSlug: string;
}
interface MemberHomePageOpts {
  orgname?: string | null;
  showGettingStarted?: boolean;
  showHomeBoardsTab?: boolean;
  templatesData?: TemplatesData;
  titleKey?: string;
}

export function memberHomePage(options: MemberHomePageOpts = {}) {
  const {
    orgname,
    showHomeBoardsTab,
    showGettingStarted,
    templatesData,
    titleKey,
  } = options;

  if (!Auth.isLoggedIn()) {
    // Hopefully you're not being routed here if you aren't logged in and
    // the server is sending you to the home page
    return errorPage({});
  }

  // We've retired the '/templates/all' endpoint. Redirect to '/templates'
  if (
    templatesData &&
    templatesData.category === 'all' &&
    !templatesData.shortLink
  ) {
    navigate('/templates', {
      trigger: true,
      replace: true,
    });
  }

  const apdexTask = (() => {
    switch (false) {
      case !showHomeBoardsTab:
        return 'memberBoards';
      default:
        return 'memberHome';
    }
  })();
  if (!templatesData) {
    Apdex.start({ task: apdexTask });
  }

  return ModelCache.waitFor('Member', Auth.myId(), () => {
    return importWithRetry(
      () => import(/* webpackChunkName: "member-page" */ './memberPage'),
    ).then(({ memberPage }) => {
      // @ts-expect-error
      const lastHomeTab = getHomeLastTab.call(this);
      // if there is no orgname or it's not on all boards tab then it means we are on '/'
      // in that case if there is a last home tab that isn't '/' we want to redirect to that
      if (
        !orgname &&
        !showHomeBoardsTab &&
        !showGettingStarted &&
        !templatesData &&
        lastHomeTab &&
        lastHomeTab !== '/' &&
        !browserNav
      ) {
        browserNav = false;

        // When a user lands on `/` and immediately gets redirected to `/u/:username/boards`,
        // we want to treat that as an "initialLoad" rather than a "transition" for Apdex
        // purposes. We need to explicitly tell the Apdex client this information, since
        // the URL change makes it look like we're doing a client-side transition even
        // though this the first rendered page the user is seeing.
        Apdex.cancel({ task: apdexTask });
        isInitial =
          Apdex.isInitialLoad && Apdex.initialUrl === window.location.href;

        return navigate(lastHomeTab, {
          trigger: true,
          replace: true,
        });
      } else if (browserNav) {
        // if navigating with browser nav buttons then we need to set the last tab
        TrelloStorage.set(getHomeLastTabStorageKey(), window.location.pathname);
      }

      if (orgname && !isMemberOfOrg(orgname)) {
        // @ts-expect-error
        return redirectHomeToDefaultTab.call(this);
      }

      trackMemberHomeView(
        orgname,
        showHomeBoardsTab,
        templatesData,
        showGettingStarted,
      );

      browserNav = false;
      // If you're logged in, always kick you to your own boards page, since you
      // can't see anyone elses
      if (simulateNewUser === null) {
        simulateNewUser = Cookies.get('simulateNewUser') || null;
      }
      const opts = {
        username: 'me',
        // we are using loadMinimumMemberDataForTemplatePage here to fetch the minimum
        // amount of member data so that we pre-cache the apollo cache. This avoids
        // making excessive requests when UpgradePromptQuery is called further down
        // the component tree
        loadFn:
          !orgname &&
          !showHomeBoardsTab &&
          templatesData &&
          'loadMinimumMemberDataForTemplatePage',
        viewClassName: 'MemberHomeView',
        getLocation: (username: PIIString | null) => {
          return getHomeUrl(
            username,
            orgname,
            showHomeBoardsTab,
            templatesData,
            showGettingStarted,
          );
        },
        titleKey: titleKey || 'boards',
        orgname,
        showHomeBoardsTab,
        showGettingStarted,
        templatesData,
      };

      return (
        memberPage
          // @ts-expect-error TS(2345): Argument of type '{ username: string; loadFn: stri... Remove this comment to see the full error message
          .call(this, opts)
          .then(() => {
            const org = ModelCache.findOne('Organization', 'name', orgname);
            const title =
              typeof orgname === 'undefined'
                ? l(['page title', 'home'])
                : !orgname && showHomeBoardsTab
                  ? l(['page title', 'boards'])
                  : org?.get('displayName');
            return setDocumentTitle(title);
          })
          .tap(function () {
            if (apdexTask !== 'memberBoards') {
              return;
            }

            if (isInitial) {
              Apdex.resetInitialState();
            }
            Apdex.stop({ task: apdexTask });

            return (isInitial = false);
          })
      );
    });
  });
}
